import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import compact from 'lodash/compact';
import GoogleMap from 'brastrap/editorial/map/Map';
import MarkDown from 'brastrap/editorial/markdown/Markdown';
import Link from 'brastrap/containers/link/LinkContainer';
import ButtonLink from 'brastrap/common/button-link/ButtonLink';

const addressLines = addr =>
  compact([
    addr.address2,
    addr.address3,
    addr.town,
    addr.region,
    addr.postCode,
  ]);
const telephoneAnchor = telephone => (
  <Link
    className="c-shop__telephone-number"
    href={`tel:${telephone.replace(/[^\d]/g, '')}`}
  >
    {telephone}
  </Link>
);

const Shop = props => {
  const showSeasonalOpeningTimes =
    props.seasonalOpeningTimesEnabled && props.seasonalOpeningTimes.length > 0;

  return (
    <div className="c-shop">
      <div className="l-grid l-grid--reverse">
        <div className="l-grid__unit l-grid__unit--content">
          <div className="s-prose s-prose--small">
            {props.location &&
              props.location.lat &&
              props.location.lon &&
              Object.keys(props.address).length > 0 && (
                <GoogleMap
                  shops={[
                    {
                      location: props.location,
                      address: addressLines(props.address),
                      label: props.name,
                    },
                  ]}
                  zoom={props.zoom}
                  width="100%"
                  googleApiKey={props.googleApiKey}
                />
              )}
          </div>
        </div>
        <div className="l-grid__unit l-grid__unit--aside">
          <div className="s-prose s-prose--small">
            <h3 className="u-subtitle">
              <FormattedMessage id="shop.address" defaultMessage="Address" />
            </h3>
            <p className="c-shop__address">
              {addressLines(props.address).map((text, index) => (
                <span key={index}>
                  {text}
                  <br />
                </span>
              ))}
            </p>

            {props.cid && (
              <ButtonLink
                label={'Get directions'}
                url={`https://maps.google.com/maps?cid=${props.cid}`}
                target="_blank"
                modifiers={['filled', 'small']}
              />
            )}

            {props.telephone && (
              <div>
                <h3 className="u-subtitle">
                  <FormattedMessage
                    id="shop.contact"
                    defaultMessage="Contact"
                  />
                </h3>
                <p>
                  <FormattedMessage
                    id="shop.telephone"
                    defaultMessage="Telephone:"
                  />{' '}
                  <span className="c-shop__telephone">
                    {telephoneAnchor(props.telephone)}
                  </span>
                </p>
                {props.contactMessage && props.country !== 'US' && (
                  <MarkDown
                    content={props.contactMessage}
                    className="s-prose--small c-shop__contact-message"
                  />
                )}
              </div>
            )}

            {props.openingTimes && props.openingTimes.length > 0 && (
              <div>
                <h3 className="u-subtitle">
                  <FormattedMessage
                    id="shop.opening-times"
                    defaultMessage="Opening Times"
                  />
                </h3>
                <table className="c-shop__opening-times">
                  <tbody>
                    <tr className="u-hidden">
                      <th scope="column">
                        <FormattedMessage
                          id="shop.opening.day"
                          defaultMessage="Day"
                        />
                      </th>
                      <th scope="column">
                        <FormattedMessage
                          id="shop.opening.times"
                          defaultMessage="Times"
                        />
                      </th>
                    </tr>
                    {props.openingTimes.map((openingTime, index) => (
                      <tr key={index}>
                        <td>{openingTime.day}</td>
                        {!openingTime.times ? (
                          <td>
                            <FormattedMessage
                              id="shop.closed"
                              defaultMessage="Closed"
                            />
                          </td>
                        ) : (
                          <td>{openingTime.times}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="l-grid">
        <div className="l-grid__unit l-grid__unit--aside">
          <div className="s-prose s-prose--small">
            {showSeasonalOpeningTimes && (
              <div className="s-prose s-prose--small">
                <h3>
                  <FormattedMessage
                    id="shop.seasonal-opening-times"
                    defaultMessage="Seasonal Opening Times"
                  />
                </h3>
                <table className="c-shop__seasonal-opening-times">
                  <tbody>
                    <tr className="u-hidden">
                      <th scope="column">
                        <FormattedMessage
                          id="shop.opening.day"
                          defaultMessage="Day"
                        />
                      </th>
                      <th scope="column">
                        <FormattedMessage
                          id="shop.opening.times"
                          defaultMessage="Times"
                        />
                      </th>
                    </tr>
                    {props.seasonalOpeningTimes.map((openingTime, index) => (
                      <tr key={index}>
                        <td>{openingTime.day}</td>
                        {!openingTime.times ? (
                          <td>
                            <FormattedMessage
                              id="shop.seasonal.closed"
                              defaultMessage="Closed"
                            />
                          </td>
                        ) : (
                          <td>{openingTime.times}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {props.servicesWeOffer && (
              <div className="s-prose s-prose--small">
                <h3 className="u-subtitle">
                  <FormattedMessage
                    id="shop.services-we-offer"
                    defaultMessage="Services We Offer"
                  />
                </h3>
                <div className="c-shop__services-we-offer">
                  <MarkDown content={props.servicesWeOffer} />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="l-grid__unit l-grid__unit--content">
          <div className="s-prose s-prose--small">
            {props.howToFindUs && (
              <div className="s-prose s-prose--small">
                <h3 className="u-subtitle">
                  <FormattedMessage
                    id="shop.how-to-find-us"
                    defaultMessage="How to find us"
                  />
                </h3>
                <div className="c-shop__how-to-find-us">
                  <MarkDown content={props.howToFindUs} />
                </div>
              </div>
            )}
            {props.localInformation && props.localInformation.length > 0 && (
              <div className="s-prose s-prose--small c-shop__local-information">
                <h3 className="u-subtitle">
                  <FormattedMessage
                    id="shop.local-information"
                    defaultMessage="Local information"
                  />
                </h3>
                {props.localInformation.map((info, index) => (
                  <MarkDown
                    key={index}
                    content={`**${info.title}:** ${info.copy}`}
                    className="s-prose s-prose--small"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const numberOrString = PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  .isRequired;

Shop.propTypes = {
  country: PropTypes.string,
  name: PropTypes.string,
  address: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    address3: PropTypes.string,
    region: PropTypes.string,
    town: PropTypes.string,
    postCode: PropTypes.string,
  }),
  cid: PropTypes.string.isRequired,
  telephone: PropTypes.string.isRequired,
  contactMessage: PropTypes.string,
  openingTimes: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.string.isRequired,
      times: PropTypes.string.isRequired,
    })
  ).isRequired,
  seasonalOpeningTimes: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.string.isRequired,
      times: PropTypes.string.isRequired,
    })
  ),
  seasonalOpeningTimesEnabled: PropTypes.bool,
  howToFindUs: PropTypes.string.isRequired,
  servicesWeOffer: PropTypes.string,
  localInformation: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      copy: PropTypes.string.isRequired,
    })
  ).isRequired,
  location: PropTypes.shape({
    lat: numberOrString,
    lon: numberOrString,
  }),
  zoom: PropTypes.number,
  googleApiKey: PropTypes.string,
};

Shop.defaultProps = {
  address: [],
  telephone: '',
  openingTimes: [],
  seasonalOpeningTimes: [],
  displaySeasonalOpeningTimes: false,
  localInformation: [],
  zoom: 16,
};

export default Shop;
